import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  Typography,
  Alert,
  InputAdornment,
  CircularProgress,
  Grid,
  FormControlLabel,
  Switch,
  Paper,
  Chip,
  Box
} from '@mui/material';
import {
  CompanyProduct,
  AllergenOption
} from '@/types/CompanyProduct.interface';
import { Company } from '@/types/Company.interface';
import FileUploadCard from '../FileUploadCard';
import NumericInput from '../NumericInput';
import { useTranslation } from 'react-i18next';
import { categoryService } from '@/data/categoryService';
import { allergenService } from '@/data/allergenService';
import { companyService } from '@/data/companyService';
import { branchService } from '@/data/branchService';
import { useUserViewMode } from '@/hooks/useUserViewMode';
import { Role } from '@/enums/role';
import { Category } from '@/types/Category.interface';
import { Branch } from '@/types/Branch.interface';
import { StockUnit } from '@/types/stock';
import { LEGAL_VAT_RATES } from '@/types/DailyLoginType.interface';
import { fetchImageAsFile } from '@/utils/imageFile';
import {
  fetchCompanyProductSettings,
  CompanyProductStrategy
} from '@/utils/companyProductPermissions';

type CompanyProductStatus = 'ACTIVE' | 'PASSIVE' | 'PENDING' | 'DELETED';

type CompanyProductDialogProps = {
  open: boolean;
  onClose: () => void;
  error?: string;
  product?: CompanyProduct | null;
  /**
   * Copy mode: a new product prefilled from this one (name gets "(Kopya)",
   * the image is copied). Ignored when `product` is set.
   */
  template?: CompanyProduct | null;
  menuMode?: boolean; // New prop to force menu creation mode
  onSave: (product: {
    name: string;
    description: string;
    basePrice: string;
    costPrice?: string;
    /** null = "Belirtilmemiş"; undefined = untouched (existing product without a loaded rate). */
    vatRate?: number | null;
    pointValue?: number;
    categoryId: number;
    isMenu: boolean;
    status: CompanyProductStatus;
    companyId: number;
    branchId?: number | null;
    allowNegativeStock: boolean;
    isStockTracked: boolean;
    trackExpiry: boolean;
    stockUnit: StockUnit;
    allergens: string[];
    allergenNote: string;
    imageFile?: File;
  }) => Promise<void>;
};

const CompanyProductDialog = ({
  open,
  onClose,
  onSave,
  error,
  product,
  template,
  menuMode = false
}: CompanyProductDialogProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [basePrice, setBasePrice] = useState(0);
  const [costPrice, setCostPrice] = useState(0);
  const [vatRate, setVatRate] = useState<number | null | undefined>(null);
  const [pointValue, setPointValue] = useState(0);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [status, setStatus] = useState<CompanyProductStatus>('PASSIVE');
  const [isMenu, setIsMenu] = useState(false);
  const [allowNegativeStock, setAllowNegativeStock] = useState(false);
  const [isStockTracked, setIsStockTracked] = useState(false);
  const [trackExpiry, setTrackExpiry] = useState(false);
  const [stockUnit, setStockUnit] = useState<StockUnit>(StockUnit.PIECE);
  const [allergens, setAllergens] = useState<string[]>([]);
  const [allergenNote, setAllergenNote] = useState('');
  const [allergenOptions, setAllergenOptions] = useState<AllergenOption[]>([]);
  const [allergenOptionsError, setAllergenOptionsError] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [copyImage, setCopyImage] = useState<'loading' | 'failed' | null>(null);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | undefined>(
    undefined
  );
  const isEditMode = !!product;

  const [companies, setCompanies] = useState<Company[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [branchId, setBranchId] = useState<number | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [productStrategy, setProductStrategy] =
    useState<CompanyProductStrategy | null>(null);

  const {
    role: userRole,
    company: currentUserCompany,
    currentBranch,
    isBranchAdmin
  } = useUserViewMode();

  // CENTRALIZED / PRICE_FLEXIBLE do not support branch-specific products
  // (backend rejects branchId with 400 under these strategies)
  const branchScopingAllowed =
    productStrategy !== CompanyProductStrategy.CENTRALIZED &&
    productStrategy !== CompanyProductStrategy.PRICE_FLEXIBLE;

  const resetForm = useCallback(() => {
    setName('');
    setBasePrice(0);
    setCostPrice(0);
    setVatRate(null);
    setPointValue(0);
    setDescription('');
    setCategoryId(null);
    setCompanyId(null);
    setBranchId(currentBranch?.id ?? null);
    setStatus('ACTIVE');
    setIsMenu(menuMode); // Set to true if in menu mode
    setAllowNegativeStock(false);
    setIsStockTracked(false);
    setTrackExpiry(false);
    setStockUnit(StockUnit.PIECE);
    setAllergens([]);
    setAllergenNote('');
    setImageFile(null);
    setCopyImage(null);
    setValidationError(undefined);
  }, [menuMode, currentBranch?.id]);

  const applyTemplate = useCallback(
    (source: CompanyProduct) => {
      setName(`${source.name} ${t('copy.suffix')}`);
      setDescription(source.description || '');
      setBasePrice(source.basePrice || 0);
      setCostPrice(source.costPrice || 0);
      setVatRate(source.vatRate ?? null);
      setPointValue(source.pointValue || 0);
      setCategoryId(source.categoryId || null);
      setCompanyId(source.companyId || null);
      setStatus(source.status === 'ACTIVE' ? 'ACTIVE' : 'PASSIVE');
      setIsMenu(source.isMenu || false);
      setAllowNegativeStock(source.allowNegativeStock || false);
      setIsStockTracked(source.isStockTracked || false);
      setTrackExpiry(source.trackExpiry || false);
      setStockUnit((source.stockUnit as StockUnit) || StockUnit.PIECE);
      setAllergens(source.allergens || []);
      setAllergenNote(source.allergenNote || '');
    },
    [t]
  );

  useEffect(() => {
    if (product) {
      setName(product.name || '');
      setDescription(product.description || '');
      setBasePrice(product.basePrice || 0);
      setCostPrice(product.costPrice || 0);
      setVatRate(product.vatRate);
      setPointValue(product.pointValue || 0);
      setCategoryId(product.categoryId || null);
      setCompanyId(product.companyId || null);
      setStatus(product.status || 'PASSIVE');
      setIsMenu(product.isMenu || false);
      setAllowNegativeStock(product.allowNegativeStock || false);
      setIsStockTracked(product.isStockTracked || false);
      setTrackExpiry(product.trackExpiry || false);
      setStockUnit((product.stockUnit as StockUnit) || StockUnit.PIECE);
      setAllergens(product.allergens || []);
      setAllergenNote(product.allergenNote || '');
    } else {
      resetForm();
    }
  }, [product, userRole, currentUserCompany, menuMode, resetForm]);

  const handleClose = () => {
    resetForm();
    setValidationError(undefined);
    onClose();
  };

  const handleSave = async () => {
    if (!name) {
      setValidationError(t('product.name.required'));
      return;
    }

    if (!basePrice) {
      setValidationError(t('product.base.price.required'));
      return;
    }

    if (!categoryId) {
      setValidationError(t('product.category.required'));
      return;
    }

    if (!companyId) {
      setValidationError(t('product.company.required'));
      return;
    }

    try {
      setLoading(true);
      await onSave({
        name,
        description,
        basePrice: basePrice.toString(),
        costPrice: costPrice > 0 ? costPrice.toString() : undefined,
        vatRate,
        pointValue: pointValue > 0 ? pointValue : undefined,
        categoryId,
        isMenu,
        status,
        companyId,
        branchId: !isEditMode && branchScopingAllowed ? branchId : undefined,
        allowNegativeStock,
        isStockTracked,
        trackExpiry,
        stockUnit,
        allergens,
        allergenNote,
        ...(imageFile && { imageFile })
      });

      // Başarılı save işleminden sonra formu temizle ve dialog'u kapat
      resetForm();
      setValidationError(undefined);
      onClose();
    } catch {
      // The caller supplies the error; keep the form and uploaded image for retry.
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);

      const params: Record<string, unknown> = { getAll: true };
      if (currentUserCompany?.id) {
        params.companyId = currentUserCompany.id;
      }

      const result = await categoryService.getAllFlat(params);
      setCategories(result || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setCategoriesLoading(false);
    }
  }, [currentUserCompany]);

  const fetchCompanies = async () => {
    try {
      setCompaniesLoading(true);
      const result = await companyService.getAllFlat({ getAll: true });
      setCompanies(result || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setCompaniesLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setValidationError(undefined);

      // Eğer yeni ürün modundaysa (product yok) formu temizle; kopyalamada
      // kaynak ürünle doldur.
      if (!product) {
        resetForm();
        if (template) applyTemplate(template);
      }

      if (userRole === Role.SUPER_ADMIN) {
        fetchCompanies();
      } else if (
        (userRole === Role.COMPANY_ADMIN || userRole === Role.BRANCH_ADMIN) &&
        currentUserCompany?.id
      ) {
        setCompanyId(currentUserCompany?.id);
      }

      fetchCategories();
    }
  }, [
    open,
    userRole,
    currentUserCompany,
    fetchCategories,
    product,
    template,
    resetForm,
    applyTemplate
  ]);

  // Kopyada görsel zorunlu: kaynağın görselini dosya olarak al. Olmazsa
  // kullanıcı yeni görsel seçer.
  useEffect(() => {
    const url = template?.file?.url;
    if (!open || product || !url) return;
    let cancelled = false;
    setCopyImage('loading');
    fetchImageAsFile(url, template.name)
      .then((file) => {
        if (cancelled) return;
        setImageFile(file);
        setCopyImage(null);
      })
      .catch(() => {
        if (!cancelled) setCopyImage('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [open, product, template]);

  // Alerjen kataloğu (servis tarafında cache'li, oturumda bir kez çekilir)
  useEffect(() => {
    if (!open || allergenOptions.length > 0) return;

    let cancelled = false;
    allergenService
      .getAllergens()
      .then((options) => {
        if (!cancelled) {
          setAllergenOptions(options);
          setAllergenOptionsError(false);
        }
      })
      .catch((err) => {
        console.error('Error fetching allergens:', err);
        if (!cancelled) setAllergenOptionsError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, allergenOptions.length]);

  const toggleAllergen = (code: string) => {
    setAllergens((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  // Branch scoping: load target company's product strategy and branch list
  useEffect(() => {
    if (!open || isEditMode || !companyId) {
      setBranches([]);
      setProductStrategy(null);
      return;
    }

    let cancelled = false;
    const loadBranchScoping = async () => {
      try {
        setBranchesLoading(true);

        const settings = await fetchCompanyProductSettings(companyId);
        if (cancelled) return;
        setProductStrategy(settings?.productStrategy ?? null);

        if (isBranchAdmin && currentBranch) {
          setBranches([currentBranch]);
        } else {
          const result = await branchService.getAllFlat({
            companyId,
            status: 'ACTIVE',
            getAll: true
          });
          if (cancelled) return;
          setBranches(result || []);
        }
      } catch (err) {
        console.error('Error fetching branches:', err);
        if (!cancelled) setBranches([]);
      } finally {
        if (!cancelled) setBranchesLoading(false);
      }
    };

    loadBranchScoping();
    return () => {
      cancelled = true;
    };
  }, [open, isEditMode, companyId, isBranchAdmin, currentBranch]);

  const handleFileSelect = (file: File) => {
    setImageFile(file);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        className="company-product-add-dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isEditMode
            ? t('edit.product')
            : menuMode
              ? t('menu.create.new')
              : t('new.product')}
          {isEditMode && ` - ${product?.name}`}
          {!isEditMode &&
            template &&
            ` - ${t('copy.of', { name: template.name })}`}
        </DialogTitle>
        <DialogContent>
          {!isEditMode && template && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('product.copy.hint')}
            </Alert>
          )}
          {copyImage === 'failed' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('copy.image.failed')}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {validationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}

          {costPrice > 0 && basePrice > 0 && costPrice > basePrice && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('warning.cost.greater.than.base.price')}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                required
                autoFocus
                margin="dense"
                label={t('name')}
                type="text"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                margin="dense"
                label={t('description')}
                type="text"
                fullWidth
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <NumericInput
                required
                margin="dense"
                label={t('base.price')}
                fullWidth
                value={basePrice}
                onChange={setBasePrice}
                allowDecimals
                decimalPlaces={2}
                min={0}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography color="text.secondary">{t('tl')}</Typography>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <NumericInput
                margin="dense"
                label={t('cost.price')}
                fullWidth
                value={costPrice}
                onChange={setCostPrice}
                allowDecimals
                decimalPlaces={2}
                min={0}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography color="text.secondary">{t('tl')}</Typography>
                    </InputAdornment>
                  )
                }}
                helperText={t('cost.price.helper')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <NumericInput
                margin="dense"
                label={t('product.point.value')}
                fullWidth
                value={pointValue}
                onChange={setPointValue}
                allowDecimals={false}
                min={0}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography color="text.secondary">
                        {t('points')}
                      </Typography>
                    </InputAdornment>
                  )
                }}
                helperText={t('product.point.value.helper')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense" required>
                <InputLabel>{t('stock.unit')}</InputLabel>
                <Select
                  value={stockUnit}
                  onChange={(e) => setStockUnit(e.target.value as StockUnit)}
                  label={t('stock.unit')}
                >
                  {Object.values(StockUnit).map((unitValue) => (
                    <MenuItem key={unitValue} value={unitValue}>
                      {t(`stock.unit.${unitValue.toLowerCase()}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense" required>
                <InputLabel>{t('category')}</InputLabel>
                <Select
                  value={
                    categories.length === 0 ||
                    !categories.some((c) => c.id === categoryId)
                      ? ''
                      : categoryId
                  }
                  onChange={(e) => setCategoryId(e.target.value as number)}
                  label={t('category')}
                  disabled={categoriesLoading}
                >
                  <MenuItem value="">
                    <em>{t('select.category')}</em>
                  </MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
                {categoriesLoading && (
                  <CircularProgress size={20} sx={{ ml: 1 }} />
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="company-product-vat-rate-label">
                  {t('product.vat.rate')}
                </InputLabel>
                <Select
                  labelId="company-product-vat-rate-label"
                  label={t('product.vat.rate')}
                  value={vatRate ?? ''}
                  onChange={(e) =>
                    setVatRate(
                      e.target.value === '' ? null : Number(e.target.value)
                    )
                  }
                >
                  <MenuItem value="">
                    <em>{t('product.vat.rate.unspecified')}</em>
                  </MenuItem>
                  {LEGAL_VAT_RATES.map((rate) => (
                    <MenuItem key={rate} value={rate}>
                      %{rate}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t('product.vat.rate.helper')}</FormHelperText>
              </FormControl>
            </Grid>

            {userRole === Role.SUPER_ADMIN && (
              <Grid item xs={12}>
                <FormControl fullWidth margin="dense" required>
                  <InputLabel>{t('company')}</InputLabel>
                  <Select
                    value={
                      companies.length === 0 ||
                      !companies.some((c) => c.id === companyId)
                        ? ''
                        : companyId
                    }
                    onChange={(e) => {
                      setCompanyId(e.target.value as number);
                      setBranchId(null);
                    }}
                    label={t('company')}
                    disabled={companiesLoading}
                  >
                    <MenuItem value="">
                      <em>{t('select.company')}</em>
                    </MenuItem>
                    {companies.map((company) => (
                      <MenuItem key={company.id} value={company.id}>
                        {company.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {companiesLoading && (
                    <CircularProgress size={20} sx={{ ml: 1 }} />
                  )}
                </FormControl>
              </Grid>
            )}

            {!isEditMode && !!companyId && branchScopingAllowed && (
              <Grid item xs={12}>
                <FormControl fullWidth margin="dense">
                  <InputLabel>{t('select.branch')}</InputLabel>
                  <Select
                    value={
                      branches.some((b) => b.id === branchId) ? branchId : ''
                    }
                    onChange={(e) =>
                      setBranchId(
                        e.target.value === ''
                          ? null
                          : (e.target.value as number)
                      )
                    }
                    label={t('select.branch')}
                    disabled={branchesLoading || isBranchAdmin}
                  >
                    {!isBranchAdmin && (
                      <MenuItem value="">
                        <em>{t('product.branch.scope.company.wide')}</em>
                      </MenuItem>
                    )}
                    {branches.map((branch) => (
                      <MenuItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {branchesLoading && (
                    <CircularProgress size={20} sx={{ ml: 1 }} />
                  )}
                </FormControl>
                {!!branchId && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {t('product.branch.scope.hint')}
                  </Alert>
                )}
              </Grid>
            )}

            {!menuMode && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isMenu}
                      onChange={(e) => setIsMenu(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={t('is.menu.help.text')}
                />
              </Grid>
            )}

            {/* Stock Settings Section */}
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderColor: 'divider',
                  borderRadius: 2,
                  backgroundColor: 'background.default'
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 2 }}
                >
                  {t('stock.settings')}
                </Typography>

                {isEditMode && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {t('product.stock.settings.edit.warning')}
                  </Alert>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isStockTracked}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            setIsStockTracked(newValue);
                            if (!newValue) {
                              setTrackExpiry(false);
                            }
                          }}
                          color="primary"
                        />
                      }
                      label={t('product.enable.stock.tracking')}
                    />
                    <Typography
                      color="text.secondary"
                      display="block"
                      sx={{ ml: 4 }}
                    >
                      {t('product.enable.stock.tracking.help')}
                    </Typography>
                  </Grid>

                  {isStockTracked && (
                    <>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={trackExpiry}
                              onChange={(e) => setTrackExpiry(e.target.checked)}
                              color="primary"
                            />
                          }
                          label={t('product.enable.batch.tracking')}
                        />
                        <Typography
                          color="text.secondary"
                          display="block"
                          sx={{ ml: 4 }}
                        >
                          {t('product.enable.batch.tracking.help')}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={allowNegativeStock}
                              onChange={(e) =>
                                setAllowNegativeStock(e.target.checked)
                              }
                              color="warning"
                            />
                          }
                          label={t('product.allow.negative.stock')}
                        />
                        <Typography
                          color="text.secondary"
                          display="block"
                          sx={{ ml: 4 }}
                        >
                          {t('product.allow.negative.stock.help')}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Paper>
            </Grid>

            {/* Allergen Section (Türk Gıda Kodeksi Ek-1) */}
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderColor: 'divider',
                  borderRadius: 2,
                  backgroundColor: 'background.default'
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 1 }}
                >
                  {t('product.allergens.section')}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  {t('product.allergens.help')}
                </Typography>

                {allergenOptionsError && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {t('product.allergens.load.error')}
                  </Alert>
                )}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {allergenOptions.map((option) => {
                    const selected = allergens.includes(option.code);
                    return (
                      <Chip
                        key={option.code}
                        label={option.name}
                        color={selected ? 'warning' : 'default'}
                        variant={selected ? 'filled' : 'outlined'}
                        onClick={() => toggleAllergen(option.code)}
                        size="small"
                      />
                    );
                  })}
                </Box>

                <TextField
                  margin="dense"
                  label={t('product.allergen.note')}
                  placeholder={t('product.allergen.note.placeholder')}
                  type="text"
                  fullWidth
                  multiline
                  rows={2}
                  inputProps={{ maxLength: 500 }}
                  value={allergenNote}
                  onChange={(e) => setAllergenNote(e.target.value)}
                />
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth margin="dense" required>
                <InputLabel>{t('status')}</InputLabel>
                <Select
                  value={status}
                  onChange={(e) =>
                    setStatus(e.target.value as CompanyProductStatus)
                  }
                  label={t('status')}
                >
                  <MenuItem value="ACTIVE">{t('status.active')}</MenuItem>
                  <MenuItem value="PASSIVE">{t('status.passive')}</MenuItem>
                  {isEditMode && (
                    <MenuItem value="PENDING">{t('status.pending')}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FileUploadCard
                onFileSelect={handleFileSelect}
                imageUrl={product?.file?.url ?? template?.file?.url}
                accept="image/*"
                helperText={
                  isEditMode ? t('edit.image.helper.text') : undefined
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            color="primary"
            disabled={loading}
            className="company-product-add-dialog-cancel-button"
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={
              !name ||
              !basePrice ||
              !categoryId ||
              !companyId ||
              (!isEditMode && !imageFile) ||
              copyImage === 'loading' ||
              loading
            }
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? t('saving') : t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompanyProductDialog;
